import { useState } from "react";
import { motion } from "framer-motion";
import { Wrench, Factory, Zap, Layers, Eye, Target } from "lucide-react";
import illu from "../assets/images/eng illu.png";
import ceoPhoto from "../assets/images/shree-ceo.jpeg";
import proprietorPhoto from "../assets/images/Tippesh.jpg";
import { Helmet } from "react-helmet";

const About = () => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [ceoLoaded, setCeoLoaded] = useState(false);

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-white">
      <Helmet>
  <title>About Shree Engineering | Technical, Manufacturing & Engineering Solutions</title>
  <meta
    name="description"
    content="Learn about Shree Engineering's journey, expertise in technical consultancy, engineering solutions, manufacturing, fabrication, heat treatment, spray booth, vacuum oven, panel board, and electrical supplies."
  />
  <meta
    name="keywords"
    content="Shree Engineering, About Us, Technical Consultancy, Engineering Solutions, Manufacturing, Fabrication, Heat Treatment, Spray Booth, Vacuum Oven, Panel Board, Electrical Supplies"
  />

  {/* Open Graph */}
  <meta
    property="og:title"
    content="About Shree Engineering | Technical, Manufacturing & Engineering Solutions"
  />
  <meta
    property="og:description"
    content="Discover Shree Engineering's expertise in technical consultancy, engineering solutions, manufacturing, fabrication, heat treatment, spray booth, vacuum oven, panel board, and electrical supplies."
  />
  <meta property="og:image" content="https://www.shree-eng.com/images/og-about.jpg" />
  <meta property="og:url" content="https://www.shree-eng.com/about" />
  <meta property="og:type" content="website" />

  {/* Twitter Card */}
  <meta
    name="twitter:card"
    content="summary_large_image"
  />
  <meta
    name="twitter:title"
    content="About Shree Engineering | Technical, Manufacturing & Engineering Solutions"
  />
  <meta
    name="twitter:description"
    content="Discover Shree Engineering's expertise in technical consultancy, engineering solutions, manufacturing, fabrication, heat treatment, spray booth, vacuum oven, panel board, and electrical supplies."
  />
  <meta name="twitter:image" content="https://www.shree-eng.com/images/og-about.jpg" />

  {/* Structured Data with all services */}
  <script type="application/ld+json">
    {`
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Shree Engineering",
      "url": "https://www.shree-eng.com",
      "logo": "https://www.shree-eng.com/images/logo.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+91-9972529464",
        "contactType": "Customer Service",
        "email": "info@shree-eng.com"
      },
      "makesOffer": [
        {"@type": "Offer","itemOffered":{"@type":"Service","name":"Technical Consultancy"}},
        {"@type": "Offer","itemOffered":{"@type":"Service","name":"Engineering Solutions"}},
        {"@type": "Offer","itemOffered":{"@type":"Service","name":"Manufacturing"}},
        {"@type": "Offer","itemOffered":{"@type":"Service","name":"Fabrication"}},
        {"@type": "Offer","itemOffered":{"@type":"Service","name":"Heat Treatment"}},
        {"@type": "Offer","itemOffered":{"@type":"Service","name":"Spray Booth"}},
        {"@type": "Offer","itemOffered":{"@type":"Service","name":"Vacuum Oven"}},
        {"@type": "Offer","itemOffered":{"@type":"Service","name":"Panel Board"}},
        {"@type": "Offer","itemOffered":{"@type":"Service","name":"Electrical Supplies"}}
      ]
    }
    `}
  </script>
</Helmet>


      {/* Background Shapes */}
      <div className="absolute top-10 left-20 w-40 h-40 bg-blue-200 rounded-full opacity-20 animate-pulse" />
      <div className="absolute bottom-20 right-20 w-60 h-60 bg-orange-200 rounded-full opacity-20 animate-bounce" />
      <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-blue-100 rounded-full opacity-10 animate-spin-slow -z-10" />

      <div className="max-w-7xl mx-auto px-6 py-20 relative z-10">
        {/* Heading */}
        <motion.h1
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-4xl md:text-5xl font-bold text-blue-900 mb-8 text-center"
        >
          About Us <span className="text-orange-500 text-lg">Since 2007</span>
        </motion.h1>

        {/* Intro Section */}
        <div className="grid md:grid-cols-4 gap-12 items-center">
          {/* Illustration */}
          <div className="md:col-span-1 flex justify-center relative">
            {!imgLoaded && (
              <div className="flex items-center justify-center w-40 h-40 md:w-56 md:h-56">
                <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            <motion.img
              src={illu}
              alt="Engineering Illustration"
              className={`w-40 md:w-56 lg:w-64 max-h-[220px] object-contain drop-shadow-lg transition-all duration-500 ${
                imgLoaded ? "opacity-100 scale-100" : "opacity-0 scale-95"
              }`}
              onLoad={() => setImgLoaded(true)}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{
                opacity: imgLoaded ? 1 : 0,
                scale: imgLoaded ? 1 : 0.95,
              }}
              transition={{ duration: 0.8 }}
            />
          </div>

          {/* Intro Text */}
          <motion.div
            className="md:col-span-3"
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9 }}
          >
            <p className="text-gray-700 leading-relaxed mb-4 text-lg font-semibold">
              Precision. Performance. Perfection.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4 text-lg">
              <span className="font-semibold text-blue-900">Shree Engineering</span>{" "}
              was founded with a vision to transform the way metal parts are
              treated, shaped, and perfected. We bring together advanced equipment,
              experienced professionals, and rigorous quality standards to serve
              critical industries with unmatched reliability.
            </p>
            <ul className="list-disc list-inside text-gray-700 text-lg mb-4 space-y-1">
              <li>High-capacity heat treatment systems</li>
              <li>CNC-enabled bending machines</li>
              <li>Certified test labs for metallurgical & structural monitoring</li>
              <li>Full-cycle fabrication and machining centers</li>
              <li>
                Custom-built industrial ovens and painting booths for precision
                finishes
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed text-lg">
              With every project, we deliver not just a product—but performance you
              can trust.
            </p>
          </motion.div>
        </div>

        {/* Expertise Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-20"
        >
          <h2 className="text-3xl font-bold text-blue-900 text-center mb-12">
            Our Expertise
          </h2>
          <div className="grid md:grid-cols-4 sm:grid-cols-2 gap-8">
            {[
              {
                icon: <Wrench className="w-10 h-10 text-blue-900 mb-4" />,
                title: "Fabrication",
                desc: "High-quality custom fabrication services with precision engineering.",
              },
              {
                icon: <Factory className="w-10 h-10 text-blue-900 mb-4" />,
                title: "Transformers",
                desc: "Reliable and efficient transformer design and manufacturing.",
              },
              {
                icon: <Zap className="w-10 h-10 text-blue-900 mb-4" />,
                title: "Electrical",
                desc: "Complete electrical solutions with modern safety standards.",
              },
              {
                icon: <Layers className="w-10 h-10 text-blue-900 mb-4" />,
                title: "Spray Booths",
                desc: "Advanced spray painting booths with durable finishing technology.",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05 }}
                className="bg-white p-6 rounded-xl shadow-lg flex flex-col items-center text-center transition"
              >
                {item.icon}
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Leadership Header */}
        <section className="mt-28">
          <motion.h2
            initial={{ opacity: 0, y: -30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-3xl md:text-4xl font-bold text-center text-blue-900 mb-12"
          >
            Leadership Insights
          </motion.h2>
        </section>

        {/* Proprietor Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-12 flex flex-col md:flex-row items-center gap-8 bg-white p-8 rounded-xl shadow-xl"
        >
          <div className="flex-shrink-0 w-40 h-40 md:w-48 md:h-48">
            <img
              src={proprietorPhoto}
              alt="Managing Director"
              className="w-full h-full object-cover rounded-full shadow-md"
            />
          </div>
          <div className="flex-1 text-gray-700 leading-relaxed text-base md:text-lg">
            <p>
              At <span className="font-semibold text-blue-900">Shree Engineering</span>, 
              every project is more than just work—it’s a promise of quality, trust, 
              and innovation. Since our inception in 2007, we have remained committed 
              to building solutions that empower industries and stand the test of time.
            </p>
            <p className="mt-3">
              Our strength lies not only in modern technology and engineering expertise 
              but also in the values we uphold—integrity, precision, and customer satisfaction.
            </p>
            <p className="mt-3">
              As we move forward, our mission remains clear: to deliver engineering excellence 
              while nurturing long-term partnerships with clients across industries.
            </p>
            <p className="mt-4 font-bold text-blue-900">Warm regards,</p>
            <p className="font-semibold">Thippesh K</p>
            <p>Managing Director</p>
            <p>Shree Engineering</p>
            <p className="text-sm">+91 99725 29464</p>
          </div>
        </motion.div>

        {/* CEO Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-12 flex flex-col md:flex-row items-center gap-8 bg-white p-8 rounded-xl shadow-xl"
        >
          <div className="flex-shrink-0 w-40 h-40 md:w-48 md:h-48 relative">
            {!ceoLoaded && (
              <div className="w-10 h-10 border-4 border-blue-900 border-t-transparent rounded-full animate-spin absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            )}
            <motion.img
              src={ceoPhoto}
              alt="CEO"
              className={`w-full h-full object-cover rounded-full transition-all duration-500 ${
                ceoLoaded ? "opacity-100 scale-100" : "opacity-0 scale-95"
              }`}
              onLoad={() => setCeoLoaded(true)}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{
                opacity: ceoLoaded ? 1 : 0,
                scale: ceoLoaded ? 1 : 0.95,
              }}
              transition={{ duration: 0.8 }}
            />
          </div>
          <div className="flex-1 text-gray-700 leading-relaxed text-base md:text-lg">
            <p>
              At Shree Engineering, innovation is not just a goal—it's our foundation. 
              As we lead the charge into the future, our commitment to cutting-edge 
              technology and advanced machinery ensures we stay ahead of the curve in 
              everything we do.
            </p>
            <p className="mt-3">
              We invest in the latest technological advancements to streamline operations, 
              enhance precision, and deliver exceptional value to our clients. Our 
              state-of-the-art machines and automated systems allow us to maintain the 
              highest standards of quality, efficiency, and sustainability.
            </p>
            <p className="mt-3">
              But technology alone is not enough. It's our people—driven by passion, 
              expertise, and vision—who bring that technology to life. Together, we're 
              building a smarter, more connected, and more efficient future.
            </p>
            <p className="mt-3 font-semibold">
              Welcome to a company where innovation powers progress.
            </p>
            <p className="mt-4 font-bold text-blue-900">Warm regards,</p>
            <p className="font-semibold">Prashant Baragi</p>
            <p>Chief Executive Officer</p>
            <p>Shree Engineering</p>
            <p className="text-sm">+966 54 764 6782<span className=" text-blue-500 text-xs font-bold px-2 py-1">International</span></p>
          </div>
        </motion.div>

        {/* Mission & Vision */}
        <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 gap-10">
          {/* Vision */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col items-center text-center bg-white p-8 rounded-xl shadow-xl"
          >
            <Eye className="w-16 h-16 text-blue-900 mb-4" />
            <h2 className="text-2xl font-bold text-blue-900 mb-2">Our Vision</h2>
            <p className="text-gray-700 text-lg leading-relaxed max-w-md">
              Innovation is our foundation. We lead the charge into the future with 
              cutting-edge technology, advanced machinery, and a commitment to excellence.
            </p>
          </motion.div>

          {/* Mission */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col items-center text-center bg-white p-8 rounded-xl shadow-xl"
          >
            <Target className="w-16 h-16 text-orange-500 mb-4" />
            <h2 className="text-2xl font-bold text-blue-900 mb-2">Our Mission</h2>
            <p className="text-gray-700 text-lg leading-relaxed max-w-md">
              To revolutionize engineering and manufacturing with innovative, sustainable, 
              and reliable solutions, while maintaining our commitment to quality, 
              safety, and customer satisfaction.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default About;
